import { submitResult, submitErrorMessage, GEO_BLOCK_MESSAGE } from "./api.ts";
import { archetypeDisplayName, fetchAggregates, PARTY_LABELS, type AggregatesResponseBody } from "./aggregates.ts";
import { DEMOGRAPHIC_OPTIONS, INTRO_COPY, RATE_MORE_BATCH, US_STATES, VOTE_TO_ADMINISTRATION } from "./config.ts";
import { buildPresidentBreakdown, type PresidentRow } from "./presidentBreakdown.ts";
import { browserShareCapabilities, shareResult } from "./share.ts";
import { sendFunnelEvent } from "./telemetry.ts";
import {
  buildSubmitPayload,
  createSession,
  currentCard,
  declineRateMore,
  finishDemographics,
  progress,
  rateCard,
  retryFromError,
  rateMore,
  remainingUnseen,
  setDemographicField,
  setError,
  skipDemographic,
  startRating,
} from "./state.ts";
import { classifyReferrer } from "./referrer.ts";
import type { Demographics, PublicDeckItem, ReferrerClass, SessionState, SubmitResponseBody } from "./types.ts";
import { generateUuidV7 } from "./uuid.ts";

declare global {
  interface Window {
    turnstile?: { render: (el: string | Element, opts: Record<string, unknown>) => string };
  }
}

// The /api/* fetch below is always same-origin — production shares one zone
// via a Cloudflare Worker Route bound to "<domain>/api/*" alongside the Pages
// custom domain (see packages/client/scripts/dev-server.mjs for the local
// dev equivalent). SHARE_BASE is only for building the (not-yet-live, Phase
// 5) /s/:token link, which is a plain anchor href, not a fetch, so it isn't
// subject to CORS and can point straight at the Worker's own origin.
const SHARE_BASE = document.querySelector("meta[name='api-base']")?.getAttribute("content") ?? "";
const TURNSTILE_SITE_KEY = document.querySelector("meta[name='turnstile-sitekey']")?.getAttribute("content") ?? "";
const app = document.getElementById("app");

function detectReferrerClass(): ReferrerClass {
  return classifyReferrer(document.referrer, window.location.origin);
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, props: Record<string, unknown> = {}, children: (Node | string)[] = []): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  Object.assign(node, props);
  for (const child of children) node.append(child);
  return node;
}

let state: SessionState;
let turnstileToken = "";
let submitId = "";
const referrerClass = detectReferrerClass();

// docs/ARCHITECTURE.md §6.7, Phase 9 D4: funnel telemetry, off the D1 write
// budget (POST /api/event -> Analytics Engine, never awaited). sessionId is
// a throwaway correlation id for this page load only — distinct from
// submitId, which is the §6.2 idempotency key and doesn't exist until the
// rater actually submits.
const telemetrySessionId = generateUuidV7();
let telemetryCompleted = false;
let telemetryDemographicsShown = false;
function emitFunnelEvent(event: "started" | "card_n" | "abandoned" | "demographics_shown" | "completed" | "rate_more", n?: number): void {
  if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return;
  sendFunnelEvent("", { event, session_id: telemetrySessionId, n }, (url, data) => navigator.sendBeacon(url, data));
}

function render(): void {
  if (!app) return;
  app.replaceChildren();

  if (state.phase === "intro") app.append(renderIntroScreen());
  else if (state.phase === "rating") app.append(renderRatingScreen());
  else if (state.phase === "rate_more") app.append(renderRateMoreScreen());
  else if (state.phase === "demographics") app.append(renderDemographicsScreen());
  else if (state.phase === "submitting") app.append(renderSubmittingScreen());
  else if (state.phase === "error") app.append(renderErrorScreen());
}

function renderIntroScreen(): HTMLElement {
  const container = el("div", { className: "screen screen--intro" });
  container.append(el("h1", { className: "intro__heading", textContent: INTRO_COPY.heading }));
  for (const paragraph of INTRO_COPY.body) {
    container.append(el("p", { className: "intro__body", textContent: paragraph }));
  }
  const startBtn = el("button", { className: "intro__start", type: "button", textContent: INTRO_COPY.cta });
  startBtn.addEventListener("click", () => {
    state = startRating(state, Date.now());
    emitFunnelEvent("started");
    render();
  });
  container.append(startBtn);
  return container;
}

function renderProgressBar(): HTMLElement {
  const { current, total } = progress(state);
  const bar = el("div", { className: "progress-bar" });
  const fill = el("div", { className: "progress-bar__fill" });
  fill.style.width = `${total === 0 ? 0 : Math.round((current / total) * 100)}%`;
  bar.append(fill);
  bar.setAttribute("role", "progressbar");
  bar.setAttribute("aria-valuenow", String(current));
  bar.setAttribute("aria-valuemax", String(total));
  return bar;
}

function renderRateMoreScreen(): HTMLElement {
  const remaining = remainingUnseen(state);
  const container = el("div", { className: "screen screen--rate-more" });
  container.append(el("h2", { textContent: "Want to rate more?" }));
  container.append(
    el("p", {
      textContent: remaining === 1 ? "There is 1 policy you haven't seen yet." : `There are ${remaining} policies you haven't seen yet.`,
    }),
  );

  const resultsBtn = el("button", { className: "rate-more__results", type: "button", textContent: "See my results" });
  resultsBtn.addEventListener("click", () => {
    state = declineRateMore(state);
    render();
  });
  const moreBtn = el("button", {
    className: "rate-more__more",
    type: "button",
    textContent: remaining < RATE_MORE_BATCH ? remaining === 1 ? "Rate the last one" : `Rate the last ${remaining}` : `Rate ${RATE_MORE_BATCH} more`,
  });
  moreBtn.addEventListener("click", () => {
    state = rateMore(state, Date.now());
    emitFunnelEvent("rate_more", state.cap);
    render();
  });
  container.append(resultsBtn, moreBtn);
  return container;
}

function renderRatingScreen(): HTMLElement {
  const card = currentCard(state);
  if (!card) {
    state = finishDemographics({ ...state, phase: "rating" });
    return renderDemographicsScreen();
  }

  const container = el("div", { className: "screen screen--rating" });
  container.append(renderProgressBar());

  const cardEl = el("div", { className: "card" });
  cardEl.append(el("p", { className: "card__text", textContent: card.text }));

  const detailEl = el("p", { className: "card__detail", hidden: true, textContent: card.detail });
  const expandBtn = el("button", { className: "card__expand", type: "button", textContent: "Expand" });
  expandBtn.addEventListener("click", () => {
    detailEl.hidden = !detailEl.hidden;
    expandBtn.textContent = detailEl.hidden ? "Expand" : "Collapse";
  });
  cardEl.append(expandBtn, detailEl);

  const scale = el("div", { className: "card__scale" });
  const options: [number, string][] = [
    [-2, "Strongly oppose"],
    [-1, "Oppose"],
    [0, "Neutral"],
    [1, "Support"],
    [2, "Strongly support"],
  ];
  for (const [value, label] of options) {
    const btn = el("button", { className: "card__scale-btn", type: "button", textContent: label });
    btn.addEventListener("click", () => {
      state = rateCard(state, value as -2 | -1 | 0 | 1 | 2, Date.now());
      emitFunnelEvent("card_n", state.currentIndex);
      render();
    });
    scale.append(btn);
  }
  cardEl.append(scale);

  container.append(cardEl);
  return container;
}

function renderSelectField(labelText: string, field: keyof Demographics, opts: readonly (readonly [string, string])[]): HTMLElement {
  const wrap = el("div", { className: "demographics__field" });
  wrap.append(el("label", { textContent: labelText }));

  const select = el("select") as HTMLSelectElement;
  select.append(el("option", { value: "", textContent: "Prefer not to say" }));
  for (const [value, text] of opts) {
    select.append(el("option", { value, textContent: text }));
  }
  select.value = state.demographics[field] ?? "";
  select.addEventListener("change", () => {
    state = select.value === "" ? skipDemographic(state, field) : setDemographicField(state, field, select.value);
  });
  wrap.append(select);
  return wrap;
}

function renderDemographicsScreen(): HTMLElement {
  if (!telemetryDemographicsShown) {
    telemetryDemographicsShown = true;
    emitFunnelEvent("demographics_shown");
  }

  const container = el("div", { className: "screen screen--demographics" });
  container.append(el("h2", { textContent: "A few optional questions" }));
  container.append(el("p", { textContent: "Every question is optional. Skipping records nothing about you." }));
  container.append(
    el("p", {
      className: "demographics__notice",
      textContent: "We ask how you voted so we can compare your ratings to others with a similar voting history. It is not used to identify you.",
    }),
  );

  if (state.error) {
    const errorEl = el("p", { className: "demographics__error", textContent: state.error });
    if (state.error === GEO_BLOCK_MESSAGE) {
      const why = el("a", { textContent: "Why?" });
      why.setAttribute("href", "/privacy/#where-youre-submitting-from");
      errorEl.append(" ", why);
    }
    container.append(errorEl);
  }

  container.append(renderSelectField("Age range", "age_range", DEMOGRAPHIC_OPTIONS.age_range));
  container.append(renderSelectField("US state", "us_state", US_STATES));
  container.append(renderSelectField("Party affiliation", "party_affiliation", DEMOGRAPHIC_OPTIONS.party_affiliation));
  container.append(renderSelectField("Who did you vote for in 2020?", "voted_2020", DEMOGRAPHIC_OPTIONS.voted_2020));
  container.append(renderSelectField("Who did you vote for in 2024?", "voted_2024", DEMOGRAPHIC_OPTIONS.voted_2024));

  const turnstileContainer = el("div", { className: "demographics__turnstile" });
  container.append(turnstileContainer);
  renderTurnstileWidget(turnstileContainer);

  const continueBtn = el("button", { className: "demographics__continue", type: "button", textContent: "See my results" });
  continueBtn.addEventListener("click", () => {
    // Stay on this screen (and keep the Turnstile widget mounted) until the
    // token actually resolves — swapping #app to the "submitting" screen
    // here would tear the widget out of the DOM before it can finish, since
    // it's now rendered inline on this screen rather than outside #app.
    continueBtn.disabled = true;
    continueBtn.textContent = "Verifying…";
    // Only mint a submitId the first time through — a retry after a failed
    // submit (e.g. Turnstile rejecting a token) must resubmit under the same
    // idempotency id, or a lost-response-but-actually-persisted case would
    // double-insert (§6.2). Only the successful path ever clears this.
    if (!submitId) submitId = generateUuidV7();
    void doSubmit();
  });
  container.append(continueBtn);
  return container;
}

function renderSubmittingScreen(): HTMLElement {
  return el("div", { className: "screen screen--submitting" }, [el("p", { textContent: "Scoring your answers…" })]);
}

function renderErrorScreen(): HTMLElement {
  const container = el("div", { className: "screen screen--error" });
  container.append(el("p", { textContent: state.error ?? "Something went wrong. Please try again." }));
  const retry = el("button", { type: "button", textContent: "Try again" });
  retry.addEventListener("click", () => {
    // The token that just failed siteverify is spent (Turnstile tokens are
    // single-use) — go back to demographics so main.ts remounts a fresh
    // widget rather than resubmitting the same doomed token.
    turnstileToken = "";
    state = retryFromError(state);
    render();
  });
  container.append(retry);
  return container;
}

/** Which administrations (reveal.administration values) the rater said they
 * voted for, derived from their voted_2020/voted_2024 answers. Empty when
 * both are skipped/"other"/"didnt_vote", or the voted-for candidate never
 * won the presidency (e.g. a 2024 Harris vote has no matching deck admin). */
function votedForAdministrations(demographics: Demographics): Set<string> {
  const out = new Set<string>();
  for (const field of ["voted_2020", "voted_2024"] as const) {
    const admin = VOTE_TO_ADMINISTRATION[demographics[field] ?? ""];
    if (admin) out.add(admin);
  }
  return out;
}

function renderPresidentRow(row: PresidentRow): HTMLElement {
  const li = el("li", { className: row.votedFor ? "reveal__president-row reveal__president-row--voted" : "reveal__president-row" });

  const nameLine = el("p", { className: "reveal__president-name" }, [row.administration]);
  if (row.votedFor) nameLine.append(el("span", { className: "reveal__voted-badge", textContent: "You voted for them" }));
  li.append(nameLine);

  const userText = row.userPct === null ? "Not enough rated items yet" : `You agreed with ${Math.round(row.userPct * 100)}%`;
  li.append(el("p", { className: "reveal__president-user", textContent: userText }));

  const partyBits = (["D", "R", "I"] as const)
    .map((party) => (row.byParty[party].pct === null ? null : `${PARTY_LABELS[party]}s: ${Math.round(row.byParty[party].pct! * 100)}%`))
    .filter((s): s is string => s !== null);
  if (partyBits.length > 0) {
    li.append(el("p", { className: "reveal__president-crowd", textContent: partyBits.join(" · ") }));
  }

  return li;
}

/** Renders (or re-renders, once crowd `aggregates` load) the "by president"
 * section into `container` without touching the rest of the reveal screen —
 * keeps the sources/details toggle's open/closed state intact across the
 * async aggregates fetch. */
function renderPresidentSection(container: HTMLElement, ratings: SessionState["ratings"], reveal: SubmitResponseBody["reveal"], votedFor: Set<string>, aggregates: AggregatesResponseBody | null): void {
  container.replaceChildren();
  const rows = buildPresidentBreakdown(ratings, reveal, aggregates, votedFor);
  if (rows.length === 0) return;

  container.append(el("h3", { className: "reveal__section-heading", textContent: "By president" }));
  const list = el("ul", { className: "reveal__president-list" });
  for (const row of rows) list.append(renderPresidentRow(row));
  container.append(list);
}

/** §3.4 [LOCKED]: a D1 write failure must still show full results — only
 * `persisted:false` hides the share link. Handled here, not as an error. */
function renderRevealScreen(result: SubmitResponseBody): void {
  if (!app) return;
  telemetryCompleted = true;
  emitFunnelEvent("completed");
  app.replaceChildren();

  const container = el("div", { className: "screen screen--reveal" });

  // "At a glance": archetype + headline stay visible without any expanding —
  // feedback round 1's "show at a glance how the user scored."
  const summary = el("div", { className: "reveal__summary" });
  summary.append(el("h2", { textContent: `You are a ${archetypeDisplayName(result.archetype)}` }));
  if (result.cross_party_pct !== null) {
    summary.append(
      el("p", { className: "reveal__headline", textContent: `You supported ${Math.round(result.cross_party_pct * 100)}% of policies from the party you didn't pick.` }),
    );
  }
  container.append(summary);

  const votedFor = votedForAdministrations(state.demographics);
  const presidentSection = el("div", { className: "reveal__presidents" });
  container.append(presidentSection);
  renderPresidentSection(presidentSection, state.ratings, result.reveal, votedFor, null);
  void fetchAggregates()
    .then((aggregates) => renderPresidentSection(presidentSection, state.ratings, result.reveal, votedFor, aggregates))
    .catch(() => {
      // Crowd comparison is a progressive enhancement on top of the user's
      // own already-rendered percentages — a failed fetch just means the
      // section stays user-only, not an error state for the whole screen.
    });

  if (result.persisted && result.share_token) {
    const shareUrl = `${SHARE_BASE}/s/${result.share_token}`;
    // Button = native share sheet (phones) or copy-link (desktop). The plain
    // anchor stays as the no-JS-API fallback and for opening the page itself.
    const shareBtn = el("button", { className: "reveal__share-btn", type: "button", textContent: "Share your result" });
    const status = el("span", { className: "reveal__share-status", role: "status" });
    shareBtn.addEventListener("click", () => {
      void shareResult(shareUrl, browserShareCapabilities()).then((outcome) => {
        status.textContent =
          outcome === "copied" ? "Link copied" : outcome === "failed" ? "Couldn't share automatically. Use the link below." : "";
      });
    });
    const link = el("a", { href: shareUrl, textContent: "Open your result page" }) as HTMLAnchorElement;
    container.append(el("div", { className: "reveal__share" }, [shareBtn, status, link]));
  }
  // persisted:false → no share link is rendered at all, per §3.4; the full
  // scored result above is unaffected either way.

  // Larger per-item text + sources stay collapsed by default (feedback round
  // 1: "the larger text and source should be hidden, but available if
  // expanded").
  const list = el("ul", { className: "reveal__list", hidden: true });
  for (const item of result.reveal) {
    const li = el("li", { className: "reveal__item" });
    li.append(el("p", { textContent: `${item.source_label} — ${item.administration} (${item.party})` }));
    li.append(el("p", { textContent: item.context }));
    const cite = el("a", { href: item.citation_url, textContent: "Source" }) as HTMLAnchorElement;
    li.append(cite);
    list.append(li);
  }

  const detailsToggle = el("button", { className: "reveal__details-toggle", type: "button", textContent: "Show sources & details" });
  detailsToggle.addEventListener("click", () => {
    list.hidden = !list.hidden;
    detailsToggle.textContent = list.hidden ? "Show sources & details" : "Hide sources & details";
  });
  container.append(detailsToggle, list);

  app.append(container);
}

async function waitForTurnstileToken(timeoutMs = 8000): Promise<void> {
  const start = Date.now();
  while (!turnstileToken && Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 100));
  }
}

async function doSubmit(): Promise<void> {
  try {
    await waitForTurnstileToken();
    state = finishDemographics(state);
    render();
    const payload = buildSubmitPayload(state, submitId, turnstileToken, referrerClass);
    const result = await submitResult(payload, { apiBase: "" });
    renderRevealScreen(result);
  } catch (err) {
    state = setError(state, submitErrorMessage(err));
    render();
  }
}

async function loadDeck(): Promise<PublicDeckItem[]> {
  const response = await fetch("/data/deck.json");
  if (!response.ok) throw new Error("Could not load the policy deck.");
  return response.json();
}

/** Explicit-render the Turnstile widget into `container` (the demographics
 * screen, right above the submit button) once api.js has loaded. Managed
 * mode can show a real interactive checkbox, unlike the old invisible setup,
 * so it needs to be somewhere on-screen and reachable, not auto-rendered
 * site-wide via a div sitting at the bottom of every page. */
function renderTurnstileWidget(container: HTMLElement): void {
  if (!TURNSTILE_SITE_KEY) return;
  const tryRender = (): void => {
    if (!window.turnstile) {
      setTimeout(tryRender, 100);
      return;
    }
    window.turnstile.render(container, {
      sitekey: TURNSTILE_SITE_KEY,
      size: "flexible",
      callback: (token: string) => {
        turnstileToken = token;
      },
    });
  };
  tryRender();
}

/** Fires "abandoned" on the way out if the rater engaged (past the intro
 * screen) but never reached the reveal screen. pagehide (not unload, which
 * blocks bfcache) is the standard reliable-exit signal sendBeacon is built
 * for; visibilitychange isn't used here since switching tabs and coming back
 * isn't abandonment. */
function registerAbandonmentBeacon(): void {
  window.addEventListener("pagehide", () => {
    if (!state || telemetryCompleted || state.phase === "intro") return;
    emitFunnelEvent("abandoned");
  });
}

async function init(): Promise<void> {
  try {
    const deck = await loadDeck();
    state = createSession(deck);
    registerAbandonmentBeacon();
    render();
  } catch (err) {
    if (app) {
      app.replaceChildren(el("p", { className: "screen screen--error", textContent: "The test couldn't load. Check your connection and reload the page." }));
    }
  }
}

void init();
